import pluralize from "pluralize";
import { ActiveCompetitionByTypeQuery } from "~graphql/sdk";
export type SocialCardVariant = "promoter" | "competition";

export interface PromoterAction {
  promoterAction: PromoterUserAction;
  entries?: number;
  socialUrl?: string;
  socialLinkType?: string;
}

interface PromoterItem {
  platform: string;
  actionType: string;
  socialLinkType?: string;
  value?: number;
}

export const isReferralPromoterAction = (
  promoterAction: PromoterUserAction
): boolean => promoterAction === "referral:code-used";

export const toPromoterAction = (
  platform: string,
  actionType: string
): PromoterUserAction => {
  const action = `${platform}:${actionType}` as const;
  if (!isPromoterUserAction(action)) {
    throw new Error(`Invalid platform action: ${action}`);
  }

  return action;
};

export const fromPromoterAction = (action: PromoterUserAction) => {
  const [platform, actionType] = action.split(":");
  return { platform, actionType };
};

export const formatPrizeNameWithArticle = (
  competition?: ActiveCompetitionByTypeQuery["activeCompetitionByType"]
): string | undefined => {
  if (!competition) return undefined;
  const prizeName = competition.prizes?.[0]?.title;
  if (!prizeName) return undefined;
  const vowels = ["a", "e", "i", "o", "u"];

  const omitA =
    prizeName.toLowerCase().startsWith("a ") ||
    prizeName.toLowerCase().startsWith("an ");

  const joinValue =
    !omitA && vowels.includes(prizeName.toLowerCase().charAt(0)) ? "an" : "a";

  const messageMiddle = omitA ? "" : `${joinValue} `;

  return `${messageMiddle}${prizeName}`;
};

export const createPromoterTitles = (
  competition?: ActiveCompetitionByTypeQuery["activeCompetitionByType"]
): {
  title: string;
  subtitle?: string;
  prizeNameWithArticle?: string;
} => {
  if (!competition) return { title: "Tell your friends you're going!" };

  const messageBeginning =
    "Share with your friends to earn more chances to win";

  const majorPrizeName = competition.prizes?.[0]?.title;
  if (!majorPrizeName) return { title: `${messageBeginning}!` };

  const subtitle = (() => {
    if (!competition?.referralRewardValue) return undefined;

    const entriesString =
      competition?.referralRewardValue > 1
        ? String(competition?.referralRewardValue)
        : "";

    return `Earn an extra ${entriesString} ${pluralize(
      "entry",
      competition.referralRewardValue
    )} for every friend who registers using your link.`;
  })();

  const prizeNameWithArticle = formatPrizeNameWithArticle(competition);

  return {
    title: `${messageBeginning} ${prizeNameWithArticle}!`,
    subtitle,
    prizeNameWithArticle,
  };
};

export const actionsForActionType = (
  items: PromoterItem[],
  inverse = false,
  ...actionType: string[]
): PromoterAction[] => {
  return items
    ?.filter(({ actionType: type }) =>
      inverse ? !actionType.includes(type) : actionType.includes(type)
    )
    ?.map((item) => {
      const result: PromoterAction = {
        socialLinkType: item.socialLinkType,
        promoterAction: toPromoterAction(item.platform, item.actionType),
      };

      if (item.value) {
        result.entries = item.value;
      }

      return result;
    });
};

const promoterUserActions = [
  "facebook:share",
  "facebook:rsvp",
  "facebook:like",
  "facebook:invite",
  "twitter:share",
  "twitter:invite",
  "email:invite",
  "instagram:follow",
  "tiktok:follow",
  "messenger:invite",
  "copy:link",
  "referral:code-used",
  "purchase:completed",
  "waitlist:join",
] as const;

export type PromoterUserAction = typeof promoterUserActions[number];

export const isPromoterUserAction = (
  action: string
): action is PromoterUserAction => {
  return promoterUserActions.includes(action as PromoterUserAction);
};

const socialUserInteractions = [
  "share-on-facebook",
  "share-on-twitter",
  "copy-link",
  "invite-on-messenger",
  "invite-on-twitter",
  "like-on-facebook",
  "rsvp-facebook",
  "follow-on-instagram",
  "follow-on-tiktok",
  "invite-via-email",
] as const;

export type SocialUserInteractions = typeof socialUserInteractions[number];
